import { Constructor } from "../Extensions";
import { IJNativeEvent } from "../JNative";
import { Control } from "../Ui/Controls/Control";
import { ControlRef } from "../Ui/Controls/ControlRef";
import { ElementRef } from "../Ui/ElementRef";
import { IJsxChildren } from "../Ui/IJsxChildren";
import { JsxElement } from "../Ui/JsxElement";
import { JsxFragment } from "../Ui/JsxFragment";

type FunctionComponent = (props: Record<string, unknown> | {}) => JsxFragment;

type JsxNode =
    boolean
    | number
    | bigint
    | string
    | null
    | undefined
    | ((evt: IJNativeEvent) => void);

type JsxHtmlAttributes = (Record<string, JsxNode | undefined> | { _data?: any, ref?: ControlRef | ElementRef }) & IJsxChildren;

namespace JSX
{
    export type HTMLAttributes = JsxHtmlAttributes;
    export type IntrinsicElements = Record<string, HTMLAttributes>;
    export type Element = JsxFragment;
}

const
    _htmlEventNames =
        Object.keys(window)
            .filter(item => /\bon/.test(item));

function createJsxElement(tag: string | FunctionComponent | Constructor<Control> | undefined, props: JSX.HTMLAttributes): JsxFragment
{
    const
        attributes: { [key: string]: any } = {},
        children = props.children ? (Array.isArray(props.children) ? props.children : [props.children]) : undefined,
        events: { [key: string]: Function } = {},
        ref = props.ref instanceof ControlRef || props.ref instanceof ElementRef ? props.ref : undefined;

    Object.keys(props)
        .forEach(key =>
        {
            if (key === "_data")
                return;

            if (key === "children")
                return;

            if (key === "ref")
                return;

            if (_htmlEventNames.indexOf(key.toLowerCase()) >= 0)
            {
                const
                    eventHandler = (props as any)[key];

                if (typeof eventHandler === "function")
                    events[key.substring(2).toLowerCase()] = (props as any)[key];

                return;
            }

            attributes[key] = (props as any)[key];
        });

    let
        jsxElement = new JsxElement();

    const
        controlConstructor =
            typeof tag === "function" && tag.prototype instanceof Control
                ? tag as unknown as Constructor<Control>
                : null;

    if (controlConstructor)
    {
        jsxElement.children = children;
        jsxElement.controlConstructor = controlConstructor;
    }
    else if (typeof tag === "function")
        jsxElement = (tag as FunctionComponent)(props);
    else
    {
        jsxElement.children = children;
        jsxElement.nodeName = tag;
    }

    if (jsxElement)
    {
        jsxElement.attributes = attributes;
        jsxElement.data = props._data;
        jsxElement.events = events;
        jsxElement.ref = ref;
    }

    return jsxElement;
}

function createJsxFragment(props: JsxHtmlAttributes): JsxFragment | undefined
{
    if (!props.children)
        return;

    const
        jsxFragment = new JsxFragment();

    jsxFragment.children = Array.isArray(props.children) ? props.children : [props.children];

    return jsxFragment;
}

export { JSX };

// Export factories
export const jsx = createJsxElement;
export const jsxs = createJsxElement;
export const jsxDEV = createJsxElement;
export const Fragment = createJsxFragment;
